import {
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  doc,
  updateDoc,
  arrayUnion,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import '@/services/firebaseService'; // ensures the Firebase app is initialized
import { BASE_URL } from '@/config/api';

export type ContactRole = 'student' | 'teacher' | 'school' | 'coach';

export interface MessagingContact {
  key: string; // lowercase email, used as participant identity
  name: string;
  email: string;
  role: ContactRole;
  imageUrl?: string;
  studentId?: string;
}

export interface DirectMessage {
  id: string;
  conversationId: string;
  from: string;
  to: string;
  senderName?: string;
  subject?: string;
  text: string;
  createdAt: Date;
  readBy: string[];
  legacy: boolean;
}

const db = () => getFirestore(getApp());
const norm = (e?: string | null) => (e || '').trim().toLowerCase();

export const conversationIdFor = (a: string, b: string) =>
  [norm(a), norm(b)].sort().join('|');

const stripHtml = (html = '') =>
  html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '').trim();

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const toMessage = (id: string, d: any): DirectMessage | null => {
  const from = norm(d.from);
  const to = norm(Array.isArray(d.to) ? d.to[0] : d.to);
  if (!from || !to) return null;
  const legacy = d.type !== 'direct';
  return {
    id,
    conversationId: d.conversationId || conversationIdFor(from, to),
    from,
    to,
    senderName: d.senderName,
    subject: d.message?.subject,
    text: d.message?.text || stripHtml(d.message?.html),
    createdAt:
      d.createdAt?.toDate?.() || d.delivery?.startTime?.toDate?.() || new Date(),
    readBy: Array.isArray(d.readBy) ? d.readBy.map(norm) : [from, to],
    legacy,
  };
};

/**
 * Subscribes to every direct message involving the current user, including
 * legacy messages sent from the student form (type: 'student').
 */
export const subscribeToConversations = (
  email: string,
  userId: string | undefined,
  callback: (messages: DirectMessage[]) => void
) => {
  const me = norm(email);
  const buckets: Record<string, Map<string, DirectMessage>> = {};
  const emit = () => {
    const all = new Map<string, DirectMessage>();
    Object.values(buckets).forEach((b) => b.forEach((m, id) => all.set(id, m)));
    callback(Array.from(all.values()).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()));
  };
  const listen = (name: string, q: any, filter?: (d: any) => boolean) =>
    onSnapshot(
      q,
      (snap: any) => {
        const map = new Map<string, DirectMessage>();
        snap.docs.forEach((s: any) => {
          const data = s.data();
          if (filter && !filter(data)) return;
          const m = toMessage(s.id, data);
          if (m && (m.from === me || m.to === me)) map.set(s.id, m);
        });
        buckets[name] = map;
        emit();
      },
      (err: any) => console.error('messaging subscription error', name, err)
    );

  const mails = collection(db(), 'mail');
  const unsubs = [
    listen('direct', query(mails, where('participants', 'array-contains', me))),
    listen('legacyTo', query(mails, where('to', '==', email)), (d) => d.type === 'student'),
  ];
  if (userId) {
    unsubs.push(
      listen('legacyFrom', query(mails, where('userId', '==', userId)), (d) => d.type === 'student')
    );
  }
  return () => unsubs.forEach((u) => u());
};

export const sendDirectMessage = async (params: {
  fromEmail: string;
  fromName: string;
  fromUserId?: string;
  to: MessagingContact;
  text: string;
}) => {
  const from = norm(params.fromEmail);
  const to = norm(params.to.email);
  await addDoc(collection(db(), 'mail'), {
    type: 'direct',
    conversationId: conversationIdFor(from, to),
    participants: [from, to],
    to: params.to.email,
    from: params.fromEmail,
    replyTo: params.fromEmail,
    senderUserId: params.fromUserId || null,
    senderName: params.fromName,
    recipientName: params.to.name,
    recipientRole: params.to.role,
    studentId: params.to.studentId || null,
    readBy: [from],
    message: {
      subject: `Nouveau message de ${params.fromName}`,
      text: params.text,
      html: escapeHtml(params.text).replace(/\n/g, '<br/>'),
    },
    createdAt: serverTimestamp(),
  });
};

export const markMessagesRead = async (ids: string[], email: string) => {
  const me = norm(email);
  await Promise.all(
    ids.map((id) => updateDoc(doc(db(), 'mail', id), { readBy: arrayUnion(me) }).catch(() => {}))
  );
};

const fullName = (p: any) =>
  p.name || [p.firstname, p.lastname].filter(Boolean).join(' ').trim() || p.email;

const toContact = (p: any, role: ContactRole): MessagingContact | null => {
  const email = p.email || p.user?.email;
  if (!email) return null;
  return {
    key: norm(email),
    email,
    name: fullName({ ...p.user, ...p }),
    role: (p.role as ContactRole) || role,
    imageUrl: p.image_url || p.avatar_url || p.user?.image_url,
    studentId: role === 'student' ? String(p.id ?? '') : undefined,
  };
};

const getJson = async (url: string, token: string) => {
  const res = await fetch(url, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
};

/**
 * Recipients allowed for the current account:
 * - Coach: own students
 * - School / school teacher: students + teachers (colleagues) + school
 * - Student of a coach: the coach; student of a school: school + its teachers
 * Primary source: GET /api/messaging/contacts. Falls back to existing endpoints.
 */
export const fetchContacts = async (user: any, token: string): Promise<MessagingContact[]> => {
  const me = norm(user?.email);
  const dedupe = (list: (MessagingContact | null)[]) => {
    const map = new Map<string, MessagingContact>();
    list.forEach((c) => c && c.key !== me && !map.has(c.key) && map.set(c.key, c));
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  try {
    const data = await getJson(`${BASE_URL}/api/messaging/contacts`, token);
    const raw = data.contacts || data.data || data;
    if (Array.isArray(raw)) return dedupe(raw.map((c: any) => toContact(c, c.role || 'student')));
  } catch {
    /* fallback below */
  }

  const type = user?.account_type;
  const result: (MessagingContact | null)[] = [];
  if (type === 'C' || type === 'S') {
    try {
      const d = await getJson(`${BASE_URL}/api/agenda/students`, token);
      (d.students || []).forEach((s: any) => result.push(toContact(s, 'student')));
    } catch { /* ignore */ }
  }
  if (type === 'S' || (type === 'C' && user?.school_id)) {
    try {
      const d = await getJson(`${BASE_URL}/api/teachers`, token);
      (d.teachers || []).forEach((t: any) => result.push(toContact(t, 'teacher')));
    } catch { /* ignore */ }
  }
  if (type === 'Student' || type === 'Parent') {
    (user?.schools || []).forEach((s: any) => {
      result.push(toContact({ ...s, name: s.school_name || s.name }, s.is_school ? 'school' : 'coach'));
      (s.teachers || []).forEach((t: any) => result.push(toContact(t, 'teacher')));
    });
  }
  return dedupe(result);
};
